import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { isDefined } from '../../../../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import { ProjectGeodataManager } from '../../../../../../viewer/project-geodata-manager';
import InspectionTool, {
  InspectionToolGeodata,
} from '../../../../../../viewer/tools/inspection-tool';
import { DataTable, DataTableColumns, DataTableSortType } from '../../../../data-table/data-table';
import { Icon } from '../../../../icon/icon';
import { IconPanel } from '../../../../icon-panel/icon-panel';
import { InfoBox } from '../../../../info-box/info-box';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { RightPanelHeader } from '../../components/right-menu-header';
import { RightPanelContent } from '../../components/styles';
import { ImageInspectionDialog } from './image-inspection-dialog';
import { ImageInspectionHover } from './image-inspection-hover';

type Props = { tool: InspectionTool };

type ImageTableItem = {
  id: string;
  name: string;
  url: string;
};

const InspectionToolMenu = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const hasAutoSelectedRef = useRef<string | undefined>(undefined);
  const [inspectionToolData, setInspectionToolData] = useState<InspectionToolGeodata>();
  const [filterText, setFilterText] = useState('');

  const [timelines] = useState(ProjectGeodataManager.instance().getTimelineContainers());

  const handleTimelineSelected = useCallback(
    async (geodataId: string) => {
      try {
        setIsLoading(true);
        setHasError(false);
        // Load geodata
        setInspectionToolData(await props.tool.loadGeodata(geodataId));
      } catch (error) {
        console.error(error);
        setHasError(true);
        setInspectionToolData(undefined);
      } finally {
        setIsLoading(false);
      }
    },
    [props.tool],
  );

  // Auto-initialize active timeline when component loads
  useEffect(() => {
    const currentTimelineContainer = ProjectGeodataManager.instance().getActiveTimelineContainer();
    const currentGeodataId = timelines.find(
      (x) => x.containerIndex === currentTimelineContainer?.containerIndex,
    )?.geodataId;

    if (timelines.length === 0 || !currentGeodataId) {
      props.tool.unloadGeodata();
      setInspectionToolData(undefined);
      setIsLoading(false);
      return;
    }

    const isInitializing = !isDefined(inspectionToolData) && isDefined(hasAutoSelectedRef.current);
    if (isInitializing || inspectionToolData?.geodataId === currentGeodataId) {
      return;
    }

    hasAutoSelectedRef.current = currentGeodataId;
    void handleTimelineSelected(currentGeodataId);
  }, [timelines, handleTimelineSelected, inspectionToolData, props.tool]);

  const images = useMemo<ImageTableItem[]>(() => {
    if (!inspectionToolData?.images) {
      return [];
    }
    return inspectionToolData.images.map((image) => {
      const displayName = image.name.substring(image.name.indexOf('_') + 1);
      return {
        id: image.id,
        name: displayName,
        url: image.url,
      };
    });
  }, [inspectionToolData?.images]);

  // Filter images by name
  const filteredImages = useMemo<ImageTableItem[]>(() => {
    const text = filterText.trim().toLowerCase();

    if (text.length === 0) {
      return images;
    }

    return images.filter((image) => image.name.toLowerCase().includes(text));
  }, [images, filterText]);

  // Define table columns
  const columns = useMemo<DataTableColumns<ImageTableItem>>(
    () => ({
      icon: {
        title: '',
        alignment: 'center',
        width: '40px',
        unfilterable: true,
        render: () => <Icon color={theme.color.green} icon={['fad', 'image']} />,
      },
      name: {
        title: t('name', { ns: 'common' }),
        sortableBy: { type: DataTableSortType.STRING },
        unfilterable: true,
      },
    }),
    [t, theme],
  );

  // Show loading state while data is loading or geodata is being initialized
  if (hasError) {
    return (
      <Stack height="100%" spacing={0}>
        <RightPanelHeader
          icon={['fal', 'magnifying-glass-plus']}
          name={t('inspectOriginalPhotos.tooltip.title', { ns: 'skyviewToolbar' })}
        />
        <RightPanelContent useTopPadding={false}>
          <Stack>
            <InfoBox color="red" leftIcon={{ icon: ['fal', 'exclamation-triangle'] }}>
              {t('inspectionTool.error.title', { ns: 'skyview' })}
            </InfoBox>
          </Stack>
        </RightPanelContent>
      </Stack>
    );
  }

  // Show loading state while data is loading or geodata is being initialized
  if (isLoading) {
    return (
      <Stack height="100%" spacing={0}>
        <RightPanelHeader
          icon={['fal', 'magnifying-glass-plus']}
          name={t('inspectOriginalPhotos.tooltip.title', { ns: 'skyviewToolbar' })}
        />
        <Stack height="100%">
          <IconPanel
            header={{ icon: { icon: ['fal', 'spinner'], opacity: '50%', spin: true, size: '4x' } }}
            responsiveness={{ compressed: false }}
          >
            {{
              title: t('inspectOriginalPhotos.tooltip.title', { ns: 'skyviewToolbar' }),
              info: t('loading', { ns: 'common' }),
            }}
          </IconPanel>
        </Stack>
      </Stack>
    );
  }

  // Show "no geodata timelines" panel if there are no timelines
  if (!isDefined(inspectionToolData)) {
    return (
      <Stack height="100%" spacing={0}>
        <RightPanelHeader
          icon={['fal', 'magnifying-glass-plus']}
          name={t('inspectOriginalPhotos.tooltip.title', { ns: 'skyviewToolbar' })}
        />
        <Stack height="100%">
          <IconPanel
            header={{
              icon: { icon: ['fad', 'magnifying-glass-plus'], opacity: '50%', size: '8x' },
            }}
            responsiveness={{ compressed: false }}
          >
            {{
              title: t('inspectOriginalPhotos.tooltip.title', { ns: 'skyviewToolbar' }),
              info: t('inspectionTool.noGeodataTimelines.title', { ns: 'skyview' }),
            }}
          </IconPanel>
        </Stack>
      </Stack>
    );
  }

  // Component loaded successfully - show header and data table
  return (
    <Stack height="100%" spacing={0}>
      <RightPanelHeader
        icon={['fal', 'magnifying-glass-plus']}
        name={t('inspectOriginalPhotos.tooltip.title', { ns: 'skyviewToolbar' })}
      />
      <RightPanelContent useTopPadding={true}>
        <Stack spacing={1}>
          <TextBox
            placeholder={t('search', { ns: 'common' })}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <DataTable
            columns={columns}
            fixedLayout={true}
            items={filteredImages}
            pageSize={15}
            onRowClicked={(item) =>
              publish(SubscriptionTopic.OpenImageInspectionDialog, {
                url: item.url,
                name: item.name,
              })
            }
          />
          <ImageInspectionDialog />
          <ImageInspectionHover />
        </Stack>
      </RightPanelContent>
    </Stack>
  );
};

export { InspectionToolMenu };
